/**
 * ActivityFeedProvider
 *
 * Single React context that owns the global activity feed state.
 * Consolidates logic previously split across useGlobalActivityStream
 * and useActivityFeed into one shared provider mounted in App.tsx.
 *
 * Fixes:
 * 1. Events persist across navigation (state lives above routes)
 * 2. Unknown (non-dextoro) tokens are filtered out
 * 3. Dedup uses time-based expiry instead of size-based pruning
 * 4. Only one global WebSocket subscription for the entire app
 * 5. Historical seed via TanStack Query with stale-while-revalidate caching
 * 6. sessionStorage warm start for instant display on return visits
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import { useWebSocket } from '@/providers/WebSocketProvider';
import { supabase } from '@/integrations/supabase/client';
import { useGlobalActivitySeed } from '@/hooks/useGlobalActivitySeed';
import type { TradeEvent } from '@/services/ws/types';
import type { ActivityEvent } from '@/types/activity';
import { playTradeSound } from '@/lib/audio/tradeSounds';

// ---- Constants ----

const DEBUG = import.meta.env.DEV;
const MAX_ITEMS = 50;
const METADATA_BATCH_DELAY_MS = 500;
const LAUNCH_POLL_INTERVAL_MS = 5000;
const LAUNCH_LOOKBACK_MS = 5 * 60 * 1000;
const DEDUP_TTL_MS = 5 * 60 * 1000;
const DEDUP_CLEANUP_INTERVAL_MS = 60 * 1000;
const UNKNOWN_SENTINEL = '__UNKNOWN__';

const SESSION_CACHE_KEY = 'dextoro_activity_feed_cache';
const SESSION_CACHE_MAX_AGE_MS = 5 * 60 * 1000;

// ---- Context ----

interface ActivityFeedContextValue {
  events: ActivityEvent[];
  isLoading: boolean;
  isConnected: boolean;
  feedSource: 'live' | 'historical' | 'mixed';
}

const ActivityFeedContext = createContext<ActivityFeedContextValue | null>(null);

export function useActivityFeedContext(): ActivityFeedContextValue {
  const ctx = useContext(ActivityFeedContext);
  if (!ctx) {
    throw new Error('useActivityFeedContext must be used within ActivityFeedProvider');
  }
  return ctx;
}

// ---- sessionStorage cache helpers ----

function loadCachedEvents(): ActivityEvent[] {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.cachedAt > SESSION_CACHE_MAX_AGE_MS) {
      sessionStorage.removeItem(SESSION_CACHE_KEY);
      return [];
    }
    return parsed.events || [];
  } catch {
    return [];
  }
}

function saveCachedEvents(events: ActivityEvent[]): void {
  try {
    sessionStorage.setItem(
      SESSION_CACHE_KEY,
      JSON.stringify({
        events: events.slice(0, 10).map(({ isNew, ...rest }) => rest),
        cachedAt: Date.now(),
      }),
    );
  } catch {
    // sessionStorage full or unavailable
  }
}

// ---- Provider ----

export function ActivityFeedProvider({ children }: { children: ReactNode }) {
  const wsContext = useWebSocket();
  const cachedEvents = useRef(loadCachedEvents()).current;
  const [events, setEvents] = useState<ActivityEvent[]>(cachedEvents);
  const [isLoading, setIsLoading] = useState(cachedEvents.length === 0);
  const [isConnected, setIsConnected] = useState(false);
  const [feedSource, setFeedSource] = useState<'live' | 'historical' | 'mixed'>('live');
  const { data: seedData } = useGlobalActivitySeed(30);

  // Time-based dedup: signature → timestamp
  const seenSignaturesRef = useRef<Map<string, number>>(new Map());
  const pendingMetadataRef = useRef<Set<string>>(new Set());
  const metadataBatchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const historicalLoadedRef = useRef(false);

  // ---- Dedup helpers ----

  function isDuplicate(sig: string): boolean {
    return seenSignaturesRef.current.has(sig);
  }

  function markSeen(sig: string): void {
    seenSignaturesRef.current.set(sig, Date.now());
  }

  function pruneOldSignatures(): void {
    const cutoff = Date.now() - DEDUP_TTL_MS;
    for (const [sig, ts] of seenSignaturesRef.current) {
      if (ts < cutoff) seenSignaturesRef.current.delete(sig);
    }
  }

  // Periodic dedup cleanup
  useEffect(() => {
    const interval = setInterval(pruneOldSignatures, DEDUP_CLEANUP_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // ---- Batch metadata resolution ----

  const resolveTokenMetadata = useCallback(
    async (mints: string[]) => {
      if (mints.length === 0 || !wsContext?.service) return;

      try {
        const { data } = await supabase
          .from('launched_tokens')
          .select('mint_address, name, symbol, image_url')
          .in('mint_address', mints);

        if (!data || !mountedRef.current) return;

        const service = wsContext.service;

        // Populate cache with found tokens
        for (const token of data) {
          service.setTokenInfo(token.mint_address, {
            name: token.name,
            symbol: token.symbol,
            imageUrl: token.image_url,
          });
        }

        // Mark mints not found in launched_tokens as unknown
        const resolvedMints = new Set(data.map((d) => d.mint_address));
        for (const mint of mints) {
          if (!resolvedMints.has(mint)) {
            service.setTokenInfo(mint, {
              name: UNKNOWN_SENTINEL,
              symbol: '',
              imageUrl: null,
            });
          }
        }

        // Re-enrich known events and filter unknown ones
        setEvents((prev) =>
          prev
            .map((e) => {
              if (!e.tokenName) {
                const info = service.getTokenInfo(e.tokenMint);
                if (info && info.name !== UNKNOWN_SENTINEL) {
                  return {
                    ...e,
                    tokenName: info.name,
                    tokenSymbol: info.symbol,
                    tokenImage: info.imageUrl,
                  };
                }
              }
              return e;
            })
            .filter((e) => {
              if (!e.tokenName) {
                const info = service.getTokenInfo(e.tokenMint);
                return !info || info.name !== UNKNOWN_SENTINEL;
              }
              return true;
            }),
        );
      } catch (err) {
        if (DEBUG) console.error('[ActivityFeedProvider] Metadata resolve error:', err);
      }
    },
    [wsContext],
  );

  const scheduleBatchResolve = useCallback(() => {
    if (metadataBatchTimerRef.current) return;
    metadataBatchTimerRef.current = setTimeout(() => {
      metadataBatchTimerRef.current = null;
      const mints = Array.from(pendingMetadataRef.current);
      pendingMetadataRef.current.clear();
      if (mints.length > 0) {
        resolveTokenMetadata(mints);
      }
    }, METADATA_BATCH_DELAY_MS);
  }, [resolveTokenMetadata]);

  // ---- Handle incoming trade ----

  const handleTrade = useCallback(
    (trade: TradeEvent) => {
      if (!mountedRef.current) return;
      if (isDuplicate(trade.txSignature)) return;
      markSeen(trade.txSignature);

      // Check if this trade is for an unknown (non-dextoro) token
      if (wsContext?.service) {
        const cached = wsContext.service.getTokenInfo(trade.tokenMint);
        if (cached && cached.name === UNKNOWN_SENTINEL) return;
      }

      // Queue metadata lookup if token info is missing
      if (!trade.tokenName && trade.tokenMint) {
        pendingMetadataRef.current.add(trade.tokenMint);
        scheduleBatchResolve();
      }

      const event: ActivityEvent = {
        txSignature: trade.txSignature,
        type: trade.type,
        trader: trade.trader,
        amountSol: trade.amountSol,
        amountTokens: trade.amountTokens,
        timestamp: trade.timestamp,
        tokenMint: trade.tokenMint,
        tokenName: trade.tokenName,
        tokenSymbol: trade.tokenSymbol,
        tokenImage: trade.tokenImage,
        isNew: true,
      };

      setEvents((prev) => {
        const updated = [event, ...prev.map((e) => ({ ...e, isNew: false }))];
        return updated.slice(0, MAX_ITEMS);
      });

      playTradeSound(trade.amountSol);
      setIsLoading(false);
    },
    [scheduleBatchResolve, wsContext],
  );

  // ---- Clear isNew flags after animation ----

  const hasNewEvents = events.some((e) => e.isNew);
  useEffect(() => {
    if (!hasNewEvents) return;

    const timeout = setTimeout(() => {
      setEvents((prev) =>
        prev.map((e) => (e.isNew ? { ...e, isNew: false } : e)),
      );
    }, 1000);
    return () => clearTimeout(timeout);
  }, [hasNewEvents]);

  // ---- Mark cached events as seen for dedup ----

  useEffect(() => {
    if (cachedEvents.length > 0) {
      for (const ev of cachedEvents) {
        markSeen(ev.txSignature);
      }
    }
  }, []);

  // ---- Historical seed via TanStack Query ----

  useEffect(() => {
    if (!seedData || historicalLoadedRef.current) return;
    historicalLoadedRef.current = true;

    const historical = seedData.events;
    if (!mountedRef.current || historical.length === 0) return;

    for (const ev of historical) {
      markSeen(ev.txSignature);
    }

    setFeedSource(seedData.source);
    setEvents((prev) => {
      if (prev.length === 0) {
        setIsLoading(false);
        return historical.slice(0, MAX_ITEMS);
      }
      const existingSigs = new Set(prev.map((e) => e.txSignature));
      const newHistorical = historical.filter(
        (e) => !existingSigs.has(e.txSignature),
      );
      const merged = [...prev, ...newHistorical];
      merged.sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
      return merged.slice(0, MAX_ITEMS);
    });
  }, [seedData]);

  // ---- Persist events to sessionStorage ----

  useEffect(() => {
    if (events.length > 0) {
      saveCachedEvents(events);
    }
  }, [events]);

  // ---- Launch event polling ----

  useEffect(() => {
    if (!wsContext?.enabled) return;

    let active = true;

    const fetchLaunches = async () => {
      try {
        const since = new Date(Date.now() - LAUNCH_LOOKBACK_MS).toISOString();
        const { data } = await supabase
          .from('launched_tokens')
          .select(
            'mint_address, name, symbol, image_url, creator_address, created_at',
          )
          .gte('created_at', since)
          .order('created_at', { ascending: false })
          .limit(10);

        if (!data || !active) return;

        const newLaunches: ActivityEvent[] = [];
        for (const token of data) {
          const sig = `launch-${token.mint_address}`;
          if (isDuplicate(sig)) continue;
          markSeen(sig);

          newLaunches.push({
            txSignature: sig,
            type: 'LAUNCH',
            trader: token.creator_address || 'unknown',
            amountSol: 0,
            amountTokens: 0,
            timestamp: token.created_at,
            tokenMint: token.mint_address,
            tokenName: token.name,
            tokenSymbol: token.symbol,
            tokenImage: token.image_url,
            isNew: true,
          });
        }

        if (newLaunches.length > 0) {
          setEvents((prev) => {
            const merged = [
              ...newLaunches,
              ...prev.map((e) => ({ ...e, isNew: false })),
            ];
            merged.sort(
              (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime(),
            );
            return merged.slice(0, MAX_ITEMS);
          });
          setIsLoading(false);
        }
      } catch (err) {
        if (DEBUG)
          console.error('[ActivityFeedProvider] Launch poll error:', err);
      }
    };

    fetchLaunches();
    const interval = setInterval(fetchLaunches, LAUNCH_POLL_INTERVAL_MS);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [wsContext?.enabled]);

  // ---- Single global WebSocket subscription ----

  useEffect(() => {
    mountedRef.current = true;

    if (!wsContext?.enabled || !wsContext.service) {
      if (DEBUG)
        console.log('[ActivityFeedProvider] WS context not available', {
          enabled: wsContext?.enabled,
          hasService: !!wsContext?.service,
        });
      return;
    }

    if (DEBUG)
      console.log(
        '[ActivityFeedProvider] Subscribing to global trade stream',
      );
    const unsub = wsContext.service.subscribeGlobal(handleTrade);

    // Track connection status
    const unsubStatus = wsContext.service.onStatusChange((status) => {
      if (DEBUG)
        console.log(`[ActivityFeedProvider] WS status changed: ${status}`);
      if (mountedRef.current) {
        setIsConnected(status === 'connected');
        if (status === 'connected') {
          setIsLoading(false);
        }
      }
    });

    // Set initial status
    const initialConnected = wsContext.service.connected;
    if (DEBUG)
      console.log(
        `[ActivityFeedProvider] Initial WS connected: ${initialConnected}`,
      );
    setIsConnected(initialConnected);
    if (initialConnected) {
      setIsLoading(false);
    }

    return () => {
      mountedRef.current = false;
      unsub();
      unsubStatus();
      if (metadataBatchTimerRef.current) {
        clearTimeout(metadataBatchTimerRef.current);
        metadataBatchTimerRef.current = null;
      }
    };
  }, [wsContext, handleTrade]);

  // ---- Provide context ----

  const value: ActivityFeedContextValue = {
    events,
    isLoading,
    isConnected,
    feedSource,
  };

  return (
    <ActivityFeedContext.Provider value={value}>
      {children}
    </ActivityFeedContext.Provider>
  );
}
